import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Logo } from '@/components/Logo';

/**
 * Handles redirects from Supabase auth emails (email confirmation, password recovery).
 * Supabase appends tokens to the URL hash; we detect type=recovery and send user to set new password.
 * If URL has ?invite=TOKEN (homeowner invite flow), after establishing session we call accept-invite then redirect to /app.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'redirecting'>('loading');

  useEffect(() => {
    const hash = window.location.hash;
    const search = window.location.search;
    const searchParams = new URLSearchParams(search);
    const inviteToken = searchParams.get('invite');

    if (!hash) {
      navigate('/login', { replace: true });
      return;
    }

    const params = new URLSearchParams(hash.slice(1)); // remove #
    const type = params.get('type');

    const run = async () => {
      await supabase.auth.getSession();
      setStatus('redirecting');
      if (type === 'recovery') {
        navigate('/auth/set-password', { replace: true });
        return;
      }
      if (inviteToken?.trim()) {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          try {
            const res = await fetch(`${supabaseUrl}/functions/v1/accept-invite`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
              body: JSON.stringify({ token: inviteToken.trim() }),
            });
            if (res.ok) {
              navigate('/app', { replace: true });
              return;
            }
          } catch {
            // fall through to /app
          }
        }
      }
      navigate('/app', { replace: true });
    };
    run();
  }, [navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-wt-bg-primary text-wt-text-primary p-4">
      <Logo variant="fullTransparent" size="h-12" className="mb-6 opacity-90" />
      <p className="font-sans text-wt-text-secondary">
        {status === 'loading' ? 'Confirming…' : 'Redirecting…'}
      </p>
    </div>
  );
}
